// #useAuthUpdated

import {
  Button,
  Dialog,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { Save } from "@material-ui/icons";
import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  Alert,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { selectFieldProps } from "../../shared/constants";
import { useAuth } from "../../../contexts/AuthContext";
import { supabase } from "../../../supabase";

/**
 * Dialog component to change the parent dealer of a dealer.
 * @param {object} props - The props object.
 * @param {boolean} props.open - Whether the dialog is open or not.
 * @param {Function} props.close - Function to call when the dialog should be closed.
 * @param {object} props.dealer - The dealer object.
 */
export default function ChangeSubDealerParent({ open, close, dealer }) {
  const { currentUser } = useAuth();
  const [dealersAbove, setDealersAbove] = useState([]);
  const [newParent, setNewParent] = useState();
  const [checked, setChecked] = useState(true);
  const [noValidParents, setNoValidParents] = useState(false);
  const [state, setState] = useState({
    loading: false,
    error: null,
  });

  const handleCheck = event => {
    setChecked(event.target.checked);
  };

  // call useEffect on any change in used dealer props
  useEffect(async () => {
    if (!dealer) {
      return;
    }

    try {
      // Get the dealer's downstream hierarchy from views_users_closure
      const { data: closureData, error: closureError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("views_users_closure")
        .select("descendant_id")
        .eq("ancestor_id", dealer.id);

      if (closureError) {
        setDealersAbove([]);
        setNoValidParents(true);
        return;
      }

      // Extract descendant IDs (the dealer's downstream hierarchy)
      const dealerDownstreamIds = closureData.map(row => row.descendant_id);

      // Get all users who are leaders, excluding:
      // 1. The dealer themselves
      // 2. Anyone in the dealer's downstream hierarchy
      const { data: allLeaders, error: leadersError } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .select("id, first_name, surname")
        .eq("is_leader", true)
        .neq("id", dealer.id)
        .not("id", "in", `(${dealerDownstreamIds.join(",")})`)
        .neq("refs_user_lifecycle_status_id", 4); // Exclude deleted users

      if (leadersError) {
        console.error("Error fetching leaders:", leadersError);
        setDealersAbove([]);
        setNoValidParents(true);
        return;
      }

      if (allLeaders && allLeaders.length > 0) {
        // Map all filtered dealers
        const filteredDealersMap = allLeaders.map(user => ({
          label: `Z-${user.id}: ${user.first_name} ${user.surname}`,
          value: user.id,
        }));

        setDealersAbove(filteredDealersMap);
        setNoValidParents(false);
      } else {
        setDealersAbove([]);
        setNoValidParents(true);
      }
    } catch (error) {
      setDealersAbove([]);
      setNoValidParents(true);
    }
  }, [dealer]);

  // Handle close dialog
  const handleClose = () => {
    setState({
      loading: false,
      error: null,
    });
    setNewParent(null);
    close();
    window.location.reload();
  };

  // handle new parent selection
  const handleSelect = v => {
    setNewParent(v);
  };
  // handle confirmation
  const handleConfirm = async () => {
    try {
      setState({
        loading: true,
        error: null,
      });
      if (!checked) {
        const { error } = await supabase
          .schema(process.env.REACT_APP_SB_SCHEMA)
          .from("users_master")
          .update({
            parent_id: dealer.parent_id,
            last_updated_by_auth_id: currentUser.auth_id,
          })
          .eq("parent_id", dealer.id);
        if (error) {
          console.error("Error updating rows:", error);
        }
      }
      const { error } = await supabase
        .schema(process.env.REACT_APP_SB_SCHEMA)
        .from("users_master")
        .update({
          parent_id: newParent.value,
          last_updated_by_auth_id: currentUser.auth_id,
        })
        .eq("id", dealer.id);
      if (error) {
        console.error(error);
        throw new Error(error);
      }
      handleClose();
    } catch (err) {
      setState({
        loading: false,
        error: err.message,
      });
    }
  };

  // return dialog
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Manager</DialogTitle>
      <DialogContent>
        {noValidParents ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No valid managers available. All eligible managers are either in
            this user's downstream hierarchy or the user themselves.
          </Alert>
        ) : (
          <>
            <Select
              {...selectFieldProps}
              options={dealersAbove}
              onChange={handleSelect}
              placeholder="Select a new manager..."
            />
            <FormControlLabel
              control={<Checkbox checked={checked} onChange={handleCheck} />}
              style={{ marginTop: "0.5rem" }}
              label="Move with children"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {state.error && (
          <Typography color="secondary">{state.error}</Typography>
        )}
        <Button
          color="secondary"
          onClick={handleClose}
          disabled={state.loading}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          variant="contained"
          startIcon={<Save />}
          disabled={state.loading || !newParent || noValidParents}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
